import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'

import ReportDocument from '@/components/ReportPDF/ReportDocument'

import Spinner from '@/shared/Spinner'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import { usePDF } from '@react-pdf/renderer'
import { toPng } from 'html-to-image'
import * as ReactDOMClient from 'react-dom/client'
import { useTranslation } from 'react-i18next'
import PDFViewerContainer from './PDFViewerContainer'
import PDFDonutChart from '@/components/ReportPDF/PDFDonutChart'
import PDFPieChart from '@/components/ReportPDF/PDFPieChart'
import PDFBarChart2 from '@/components/ReportPDF/PDFBarChart2'
import PDFBarChart3 from '@/components/ReportPDF/PDFBarChart3'
import PDFBarChart4 from '@/components/ReportPDF/PDFBarChart4'

const mapStateToProps = ({ report }) => ({ data: report.sampleData })

const FirstRenderDoc = ({ download, onDownload, ...props }) => {
  usePDF({
    document: <ReportDocument {...props} />,
  })

  return null
}

const loadFirstRenderDoc = ({ onRender, ...props }) => {
  const helperDiv = document.createElement('tmp')
  const newRoot = ReactDOMClient.createRoot(helperDiv)

  const doc = (
    <FirstRenderDoc
      {...props}
      onRender={onRenderProps => {
        onRender?.(onRenderProps)
        newRoot?.unmount()
        helperDiv?.remove()
      }}
    />
  )

  newRoot.render(<React.StrictMode>{doc}</React.StrictMode>)
}

const loadDonutChart = ({ setDonutChartImg, data }) => {
  const helperDiv = document.createElement('tmp')
  const newRoot = ReactDOMClient.createRoot(helperDiv)

  const {
    onlyOpenCount,
    onlyLinkCount,
    onlyFileCount,
    openLinkCount,
    openFileCount,
    linkFileCount,
    linkTextCount,
    threeStatesCount,
    phishCount,
  } = data

  const convertData = [
    { name: '僅瀏覽郵件', value: onlyOpenCount },
    { name: '僅點閱連結', value: onlyLinkCount },
    { name: '僅開啟附件', value: onlyFileCount },
    { name: '瀏覽郵件 + 點閱連結', value: openLinkCount },
    { name: '瀏覽郵件 + 開啟附件', value: openFileCount },
    { name: '點閱連結 + 開啟附件', value: linkFileCount },
    { name: '點閱連結 + 輸入資料', value: linkTextCount },
    { name: '三種釣中行為(含)以上', value: threeStatesCount },
  ]

  const chart = (
    <PDFDonutChart
      label={phishCount}
      data={convertData}
      onLoaded={node => {
        if (node) {
          // 確保圖表完全渲染後執行
          setTimeout(() => {
            toPng(node, { height: 650, width: 800 }).then(img => {
              setDonutChartImg?.(img)
              newRoot?.unmount()
              helperDiv?.remove()
            })
          }, 5000)
        }
      }}
    />
  )

  newRoot.render(<React.StrictMode>{chart}</React.StrictMode>)
}

const loadPieChart = ({ setPieChartImg, data }) => {
  const helperDiv = document.createElement('tmp')
  const newRoot = ReactDOMClient.createRoot(helperDiv)

  // const data = [
  //   { name: '中興廠', value: 24 },
  //   { name: '北興廠', value: 40 },
  //   { name: '中興AIT廠', value: 5 },
  //   { name: '湖口廠', value: 29 },
  // ]

  const convertData = data.map(group => ({
    name: group.groupName,
    value: group.phishCount,
  }))

  const chart = (
    <PDFPieChart
      data={convertData}
      onLoaded={node => {
        if (node) {
          // 確保圖表完全渲染後執行
          setTimeout(() => {
            toPng(node, { height: 450, width: 700 }).then(img => {
              setPieChartImg?.(img)
              newRoot?.unmount()
              helperDiv?.remove()
            })
          }, 5000)
        }
      }}
    />
  )

  newRoot.render(<React.StrictMode>{chart}</React.StrictMode>)
}

const loadPieChart2 = ({ setPieChart2Img, data }) => {
  const helperDiv = document.createElement('tmp')
  const newRoot = ReactDOMClient.createRoot(helperDiv)

  // const data = [
  //   { name: 'Windows', value: 94, rank: 1 },
  //   { name: 'Andriod', value: 6, rank: 2 },
  //   { name: 'ios', value: 2, rank: 3 },
  // ]

  const convertData = data.map(group => ({
    name: group.osName,
    value: group.useCount,
  }))

  const chart = (
    <PDFPieChart
      data={convertData}
      onLoaded={node => {
        if (node) {
          // 確保圖表完全渲染後執行
          setTimeout(() => {
            toPng(node, { height: 450, width: 700 }).then(img => {
              setPieChart2Img?.(img)
              newRoot?.unmount()
              helperDiv?.remove()
            })
          }, 5000)
        }
      }}
    />
  )

  newRoot.render(<React.StrictMode>{chart}</React.StrictMode>)
}

const loadBarChart2 = ({ setBarChart2Img, data }) => {
  const helperDiv = document.createElement('tmp')
  const newRoot = ReactDOMClient.createRoot(helperDiv)

  // const data = [
  //   {
  //     scheduledStartTime: '2023-10-09T00:00:00.000+08:00',
  //     groupNumber: 100,
  //     openEmail: 40,
  //     linkedClicked: 35,
  //     attachmentOpened: 5,
  //     enterData: 20,
  //   },
  //   {
  //     scheduledStartTime: '2024-05-09T00:00:00.000+08:00',
  //     groupNumber: 150,
  //     openEmail: 50,
  //     linkedClicked: 25,
  //     attachmentOpened: 25,
  //     enterData: 50,
  //   },
  // ]

  const convertData = data.map(item => ({
    scheduledStartTime: item.year,
    groupNumber: item.report.userCount,
    openEmail: item.report.openCount,
    linkedClicked: item.report.linkCount,
    attachmentOpened: item.report.fileCount,
    enterData: item.report.textCount,
  }))

  const chart = (
    <PDFBarChart2
      data={convertData}
      onLoaded={node => {
        if (node) {
          // 確保圖表完全渲染後執行
          setTimeout(() => {
            toPng(node, { height: 650, width: 800 }).then(img => {
              setBarChart2Img?.(img)
              newRoot?.unmount()
              helperDiv?.remove()
            })
          }, 5000)
        }
      }}
    />
  )

  newRoot.render(<React.StrictMode>{chart}</React.StrictMode>)
}

const loadBarChart3 = ({ setBarChart3Img, data }) => {
  const helperDiv = document.createElement('tmp')
  const newRoot = ReactDOMClient.createRoot(helperDiv)

  // const data = [
  //   {
  //     scheduledStartTime: '2023-10-09T00:00:00.000+08:00',
  //     groupNumber: 100,
  //     openEmail: 40,
  //     linkedClicked: 35,
  //     attachmentOpened: 5,
  //     enterData: 20,
  //     tempId: 1,
  //   },
  //   {
  //     scheduledStartTime: '2024-05-09T00:00:00.000+08:00',
  //     groupNumber: 150,
  //     openEmail: 50,
  //     linkedClicked: 25,
  //     attachmentOpened: 25,
  //     enterData: 50,
  //     tempId: 2,
  //   },
  //   {
  //     scheduledStartTime: '2024-09-09T00:00:00.000+08:00',
  //     groupNumber: 230,
  //     openEmail: 70,
  //     linkedClicked: 100,
  //     attachmentOpened: 10,
  //     enterData: 50,
  //     tempId: 3,
  //   },
  //   // {
  //   //   campaignId: null,
  //   //   scheduledStartTime: '2024-10-09T00:00:00.000+08:00',
  //   //   groupNumber: 230,
  //   //   openEmail: 70,
  //   //   linkedClicked: 100,
  //   //   attachmentOpened: 10,
  //   //   enterData: 50
  //   // }
  // ]

  const convertData = data.map(item => ({
    scheduledStartTime: item.year,
    groupNumber: item.report.userCount,
    openEmail: item.report.openCount,
    linkedClicked: item.report.linkCount,
    attachmentOpened: item.report.fileCount,
    enterData: item.report.textCount,
    tempId: item.tempId,
  }))

  const chart = (
    <PDFBarChart3
      data={convertData}
      onLoaded={node => {
        if (node) {
          // 確保圖表完全渲染後執行
          setTimeout(() => {
            toPng(node, { height: 650, width: 800 }).then(img => {
              setBarChart3Img?.(img)
              newRoot?.unmount()
              helperDiv?.remove()
            })
          }, 5000)
        }
      }}
    />
  )

  newRoot.render(<React.StrictMode>{chart}</React.StrictMode>)
}

const loadBarChart4 = ({ setBarChart4Img, data }) => {
  const helperDiv = document.createElement('tmp')
  const newRoot = ReactDOMClient.createRoot(helperDiv)

  // const data = [
  //   {
  //     totalNumber: 15,
  //     notCaught: 9,
  //     caught: 6,
  //     depart: '1200900廠務部',
  //   },
  //   {
  //     totalNumber: 25,
  //     notCaught: 19,
  //     caught: 6,
  //     depart: '1280300中興測試工程部',
  //   },
  //   {
  //     totalNumber: 50,
  //     notCaught: 39,
  //     caught: 11,
  //     depart: '12C07000北興測試產品二部',
  //   },
  //   {
  //     totalNumber: 43,
  //     notCaught: 9,
  //     caught: 34,
  //     depart: '1290300北興測試產品二部',
  //   },
  //   {
  //     totalNumber: 57,
  //     notCaught: 39,
  //     caught: 18,
  //     depart: '1390300記憶體工程二部',
  //   },
  //   {
  //     totalNumber: 74,
  //     notCaught: 25,
  //     caught: 49,
  //     depart: '1130100人事行政處-北興',
  //   },
  //   {
  //     totalNumber: 47,
  //     notCaught: 39,
  //     caught: 8,
  //     depart: '1130300人事行政處-湖口',
  //   },
  //   {
  //     totalNumber: 19,
  //     notCaught: 4,
  //     caught: 15,
  //     depart: '1150200會計部',
  //   },
  //   {
  //     totalNumber: 7,
  //     notCaught: 3,
  //     caught: 4,
  //     depart: '1290100北興測試製造部',
  //   },
  //   {
  //     totalNumber: 67,
  //     notCaught: 27,
  //     caught: 40,
  //     depart: '1390300類比C測試工程二部',
  //   },
  // ]

  const convertData = data.map(item => ({
    totalNumber: item.phishCount + item.safeCount,
    notCaught: item.safeCount,
    caught: item.phishCount,
    depart: item.groupName,
  }))

  const chart = (
    <PDFBarChart4
      data={convertData}
      onLoaded={node => {
        if (node) {
          // 確保圖表完全渲染後執行
          setTimeout(() => {
            toPng(node, { height: 650, width: 800 }).then(img => {
              setBarChart4Img?.(img)
              newRoot?.unmount()
              helperDiv?.remove()
            })
          }, 5000)
        }
      }}
    />
  )

  newRoot.render(<React.StrictMode>{chart}</React.StrictMode>)
}

const ReportSample = ({
  dispatch,
  data: reportData,
  phishType,
  generateStartDate,
  generateEndDate,
}) => {
  const { t } = useTranslation()

  const [pdfDoc, setPdfDoc] = useState()

  const [donutChartImg, setDonutChartImg] = useState()
  const [pieChartImg, setPieChartImg] = useState()
  const [barChart2Img, setBarChart2Img] = useState()
  const [barChart3Img, setBarChart3Img] = useState()
  const [barChart4Img, setBarChart4Img] = useState()
  const [pieChart2Img, setPieChart2Img] = useState()

  const primaryCompanyNickname = '甲方公司簡稱'
  const secondaryCompanyNickname = '乙方公司簡稱'
  const secondaryCompanyFullname = '乙方公司全名'

  useEffect(() => {
    dispatch({ type: 'report/getSampleData' })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (reportData) {
      loadDonutChart({ setDonutChartImg, data: reportData.actionGroup || {} })
      loadPieChart({ setPieChartImg, data: reportData.officeReportGroups || [] })
      loadPieChart2({ setPieChart2Img, data: reportData.osCounts || [] })
      loadBarChart2({ setBarChart2Img, data: reportData.reprotHistories || [] })
      loadBarChart3({ setBarChart3Img, data: reportData.tempResults || [] })
      loadBarChart4({ setBarChart4Img, data: reportData.deptReportGroups || [] })
    }
  }, [reportData])

  useEffect(() => {
    if (
      donutChartImg &&
      pieChartImg &&
      pieChart2Img &&
      barChart2Img &&
      barChart3Img &&
      barChart4Img &&
      !pdfDoc
    ) {
      loadFirstRenderDoc({
        donutChartImg,
        pieChartImg,
        pieChart2Img,
        barChart2Img,
        barChart3Img,
        barChart4Img,
        reportData: {
          ...reportData,
          phishType: t(`general.${phishType === 'M' ? 'mail' : 'sms'}`),
          primaryCompanyNickname,
          secondaryCompanyNickname,
          secondaryCompanyFullname,
        },
        onRender: ({ pageData }) => {
          setPdfDoc(
            <ReportDocument
              donutChartImg={donutChartImg}
              pieChartImg={pieChartImg}
              pieChart2Img={pieChart2Img}
              barChart2Img={barChart2Img}
              barChart3Img={barChart3Img}
              barChart4Img={barChart4Img}
              pageData={pageData}
              reportData={{
                ...reportData,
                phishType: t(`general.${phishType === 'M' ? 'mail' : 'sms'}`),
                primaryCompanyNickname,
                secondaryCompanyNickname,
                secondaryCompanyFullname,
                generateStartDate,
                generateEndDate,
              }}
            />,
          )
        },
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [donutChartImg, pieChartImg, pieChart2Img, barChart2Img, barChart3Img, barChart4Img, pdfDoc])

  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      {/* <WordPreview
        barChartImg={barChartImg}
        lineChartImg={lineChartImg}
        reportData={{
          ...data,
          phishType: t('general.mail'),
        }}
      /> */}

      {pdfDoc ? (
        <Container
          maxWidth={false}
          sx={{
            height: '100%',
            padding: 0,
            paddingLeft: '0 !important',
            paddingRight: '0 !important',
          }}
        >
          <PDFViewerContainer document={pdfDoc} />
        </Container>
      ) : (
        <Stack sx={{ height: 200, width: 200 }}>
          <Spinner />
        </Stack>
      )}
    </div>
  )
}

export default connect(mapStateToProps)(ReportSample)
